import OpenSeadragon, {
  CanvasClickEvent,
  CanvasDoubleClickEvent,
  CanvasDragEvent,
  CanvasPressEvent,
  EventHandler,
  Point,
  ViewerEventMap,
} from "openseadragon";
import { v4 } from "uuid";

import { ScaleInfo, ViewerMode, XYPoint } from "custom-models/ViewerTypes";

import { ExtendedSeadragonViewer } from "shared/utils/openseadragon/ExtendedSeadragon";

class ViewerFacade {
  private viewer: ExtendedSeadragonViewer | null;
  private readonly setViewerModeCallback: (mode: ViewerMode) => void;

  private wsiWidth: number;
  private wsiHeight: number;
  private dziOffset: XYPoint;

  private readonly handlerMap = new Map<
    string,
    {
      eventType: keyof ViewerEventMap;
      handler: EventHandler<ViewerEventMap[keyof ViewerEventMap]>;
    }
  >();

  constructor(
    viewer: ExtendedSeadragonViewer,
    setViewerModeCallback: (mode: ViewerMode) => void
  ) {
    this.viewer = viewer;
    this.setViewerModeCallback = setViewerModeCallback;
    this.wsiWidth = 0;
    this.wsiHeight = 0;
    this.dziOffset = { x: 0, y: 0 };
  }

  // For the benefit of event listeners within the SVG tree
  setViewerMode(mode: ViewerMode) {
    this.setViewerModeCallback(mode);
  }

  setWsiDWidth(width: number) {
    this.wsiWidth = width;
  }

  setWsiHeight(height: number) {
    this.wsiHeight = height;
  }

  setDziOffset(offset: XYPoint) {
    this.dziOffset = offset;
  }

  getZoom = (): number => {
    if (!this.viewer) return 0;
    return this.viewer?.viewport.getZoom(true);
  };

  getRotation = (): number => {
    // @ts-ignore
    return this.viewer.viewport.getRotation() || 0;
  };

  addHandler = <T extends keyof ViewerEventMap>(
    eventType: T,
    handler: EventHandler<ViewerEventMap[T]>
  ): string => {
    const handlerId = v4();
    this.handlerMap.set(handlerId, { eventType, handler } as {
      eventType: keyof ViewerEventMap;
      handler: EventHandler<ViewerEventMap[keyof ViewerEventMap]>;
    });
    this.viewer?.addHandler(eventType, handler);
    return handlerId;
  };

  removeHandler = (handlerId: string): void => {
    const handler = this.handlerMap.get(handlerId);
    if (handler) {
      this.handlerMap.delete(handlerId);
      this.viewer?.removeHandler(
        handler.eventType,
        handler.handler as EventHandler<any>
      );
    }
  };

  removeAllHandlers = () => {
    this.handlerMap.forEach((_, handlerId) => {
      this.removeHandler(handlerId);
    });
  };

  extractScaleInfo = (): ScaleInfo | undefined => {
    if (!this.viewer) return;
    const viewer = this.viewer;

    const tiledImage = viewer?.world.getItemAt(0);
    if (!tiledImage) {
      return undefined;
    }

    // viewport coordinates
    const { width: viewportWidth, height: viewportHeight } =
      tiledImage.getBounds();

    // These are in image coordinates
    const topLeft = viewer.viewport.viewportToImageCoordinates(
      new OpenSeadragon.Point(0, 0)
    );
    const bottomRight = viewer.viewport.viewportToImageCoordinates(
      new OpenSeadragon.Point(viewportWidth, viewportHeight)
    );
    const width = bottomRight.x - topLeft.x;
    const height = bottomRight.y - topLeft.y;

    // Calculate scaleX and scaleY (should be same)
    // We could simply use the viewer element ("web") coordinates:
    // const vTopLeft = viewer.viewport.viewportToViewerElementCoordinates(new OpenSeadragon.Point(0, 0));
    // const vBottomRight = viewer.viewport.viewportToViewerElementCoordinates(new OpenSeadragon.Point(viewportWidth, viewportHeight));
    // But we don't want the rotation being taken into account
    const vTopLeft = viewer.viewport.pixelFromPointNoRotate(
      new OpenSeadragon.Point(0, 0),
      true
    );
    const vBottomRight = viewer.viewport.pixelFromPointNoRotate(
      new OpenSeadragon.Point(viewportWidth, viewportHeight),
      true
    );

    const vWidth = vBottomRight.x - vTopLeft.x;
    const vHeight = vBottomRight.y - vTopLeft.y;

    // These two should always be the same but we're pretending they might be different
    const scaleX = vWidth / width;
    const scaleY = vHeight / height;

    // Now for upstream information- the DZI is offset relative to the original WSI.
    // This is relevant to the AI overlay which uses the WSI coordinates.
    return {
      width,
      height,
      scaleX,
      scaleY,
      rotation: this.getRotation(),
      wsiWidth: this.wsiWidth,
      wsiHeight: this.wsiHeight,
      dziOffsetX: this.dziOffset?.x || 0,
      dziOffsetY: this.dziOffset?.y || 0,
    };
  };

  imageCoords = (
    event:
      | CanvasPressEvent
      | CanvasDragEvent
      | CanvasClickEvent
      | CanvasDoubleClickEvent,
    round: boolean = true
  ): Point | null => {
    if (!this.viewer) return null;
    if (event.position) {
      const point = this.viewer.viewport.viewerElementToImageCoordinates(
        event.position
      );
      if (round) {
        point.x = Math.round(point.x);
        point.y = Math.round(point.y);
      }
      return point;
    }
    return null;
  };

  getPixelsPerMeter = (): number | undefined =>
    this.viewer?.getPixelsPerMeter();

  destroy = () => {
    this.removeAllHandlers();
    this.viewer?.destroy();
    this.viewer = null;
  };
}

export default ViewerFacade;
